import type { Budget, BudgetStatus } from '@orgus/protocol';
import { Pencil, Plus, Target } from '@orgus/ui/icons';
import { Button, EmptyState, IconButton, Panel, REVEAL_GROUP, RevealRest, RevealSlot, RowStatus, Tooltip, cn, formatMoney, formatPercent, useNow } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { PERIOD_LABEL, budgetStateLabel, periodEndLabel } from './model.ts';
import type { BudgetState } from './model.ts';

const FILL: Record<BudgetState, string> = { ok: 'bg-(--meter-fill) prism-ray-progress', soft: 'bg-(--meter-fill-warn)', hard: 'bg-(--meter-fill-over)' };

/** A budget's one status: on track is quiet; near the limit, or a forecast over it, needs you; over the limit failed. */
export function budgetRowStatus(state: BudgetState, forecastOver = false): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const label = budgetStateLabel(state, forecastOver);
  return state === 'hard'
    ? { tone: 'danger', icon: 'failed', label }
    : state === 'soft' || forecastOver
      ? { tone: 'attention', icon: 'warning', label }
      : { tone: 'neutral', icon: 'done', label };
}

/** 'ends Oct 31' after 'Monthly, ': the shared date text, with only its first letter lowered. */
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/**
 * One budget: its status, a burn meter scaled to the limit (the fill is the share spent) with
 * the warning point and a forecast marker, and when it resets. Only a monthly budget carries a
 * forecast: a day is too short to project, and a total has no end.
 */
export function BurnBar({ budget, status, highlighted, onEdit }: { budget: Budget; status: BudgetStatus; highlighted?: boolean; onEdit?: () => void }) {
  const now = useNow(60_000);
  const span = Math.max(status.limit, status.spent) || 1;
  const spent = Math.min(1, status.spent / span);
  const soft = (budget.softAt * status.limit) / span;
  const forecast = budget.period === 'month';
  const forecastAt = Math.min(1, status.forecast / span);
  const policy = `Warns at ${formatPercent(budget.softAt)}. ${budget.onLimit === 'stop' ? 'Stops launched sessions at the limit.' : 'Only warns at the limit.'}`;
  return (
    <li id={`budget-${budget.id}`} className={cn(REVEAL_GROUP, 'motion-arrive relative flex flex-col gap-2 rounded-control px-3 py-3 transition-colors duration-(--duration-fast) hover:bg-hover', highlighted && 'bg-active')}>
      <div className="flex min-w-0 items-center gap-3">
        <span className="min-w-0 flex-1 truncate text-ui font-medium text-fg">{status.label}</span>
        <RevealRest className="flex shrink-0 justify-end">
          <Tooltip content={policy}>
            <span tabIndex={0}>
              <RowStatus {...budgetRowStatus(status.state, forecast && status.forecastOver)} />
            </span>
          </Tooltip>
        </RevealRest>
        {onEdit && (
          <RevealSlot className="absolute top-2 right-3">
            <IconButton label={`Edit budget ${status.label}`} icon={<Pencil />} size="xs" onClick={onEdit} />
          </RevealSlot>
        )}
      </div>
      <div
        className="relative h-(--meter-track-height) overflow-hidden rounded-pill bg-(--meter-track)"
        role="meter"
        aria-label={`${status.label}: ${formatMoney(status.spent)} of ${formatMoney(status.limit)}`}
        aria-valuemin={0}
        aria-valuemax={Math.round(status.limit * 100) / 100}
        aria-valuenow={Math.round(status.spent * 100) / 100}
      >
        <span aria-hidden className={cn('motion-grow-x absolute inset-0 rounded-pill', FILL[status.state])} style={{ scale: `${spent} 1` }} />
        <span aria-hidden className="absolute inset-y-0 w-px bg-fg-subtle" style={{ left: `${soft * 100}%` }} />
        {/* The forecast is a marker on a neutral track, never a tinted wash over it. */}
        {forecast && status.forecast > status.spent && (
          <span aria-hidden className={cn('absolute inset-y-0 w-0.5 -translate-x-full rounded-pill', FILL[status.forecastOver ? 'hard' : status.state])} style={{ left: `${forecastAt * 100}%` }} />
        )}
      </div>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 text-caption text-fg-subtle">
        <span className="num">
          <span className="text-ui text-fg">{formatMoney(status.spent)}</span> of {formatMoney(status.limit)}
          {forecast && (
            <span className={cn(status.forecastOver && status.state !== 'hard' && 'text-status-attention')}>
              , {formatMoney(status.forecast)} forecast
            </span>
          )}
        </span>
        <span className="ml-auto shrink-0">
          {PERIOD_LABEL[budget.period]}, {lowerFirst(periodEndLabel(status, now))}
        </span>
      </div>
    </li>
  );
}

export function BudgetList({
  budgets,
  statuses,
  highlight,
  onAdd,
  onEdit,
  className,
}: {
  budgets: Budget[];
  statuses: BudgetStatus[];
  highlight?: string | null;
  onAdd: () => void;
  onEdit: (b: Budget) => void;
  className?: string;
}) {
  const order: Record<BudgetState, number> = { hard: 0, soft: 1, ok: 2 };
  const rows = statuses
    .map((s) => ({ s, b: budgets.find((x) => x.id === s.budgetId) }))
    .filter((r): r is { s: BudgetStatus; b: Budget } => !!r.b)
    .sort((a, z) => order[a.s.state] - order[z.s.state] || z.s.spent / z.s.limit - a.s.spent / a.s.limit);
  return (
    <Panel title="Budgets" sub={rows.length || undefined} icon={<Target />} flush className={className}>
      {rows.length === 0 ? (
        <EmptyState
          icon={<Target />}
          title="No budgets yet"
          description="Limit spend for everything, a project, a session or a mission."
          action={
            <Button variant="secondary" icon={<Plus />} onClick={onAdd}>
              Add budget
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-1 px-1 pb-1">
          {rows.map(({ s, b }) => (
            <BurnBar key={b.id} budget={b} status={s} highlighted={highlight === b.id} onEdit={() => onEdit(b)} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
